import React from 'react'
import * as yup from 'yup'
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom'
import { Alert, Button, Col, Container, Dropdown, Row } from 'react-bootstrap'
import { Formik } from 'formik'
import { MoreHorizontal } from 'react-feather'

import { AppContext } from '../../App'
import { Loading } from '../../components/ui/Loading/Loading'
import InvoiceInfoCard from '../../components/invoice/invoiceInfoCard'
import { FormNumber } from '../../components/ui/Form/Number'
import { FormText } from '../../components/ui/Form/Text'
import { PageStatus } from '../../constants/pageStatus'
import * as Request from '../../utilities/request'
import { Invoice } from '../../../../back-end/common/invoice'

import './invoicePrepareLineItems.css'
import { errorMsgFromFormik } from '../../utilities/form'
import { roundToTwo } from '../../utilities/format'
import LineItemHistoryPanel from '../../components/invoice/lineItemHistoryPanel'
import { getTotalsFromTaxType } from './invoiceAllocation'
import { invoiceState } from '../../utilities/invoice'
import { XeroAccount } from '../../../../back-end/common/xeroAccount'
import { XeroTaxRate } from '../../../../back-end/common/xeroTaxRate'
import { FormSelect, SelectOption } from '../../components/ui/Form/Select'
import { useInterval } from 'usehooks-ts'

const invoiceValidationSchema = yup.object().shape({
	lineItems: yup
		.array()
		.of(
			yup.object().shape({
				description: yup.string().required('Description is Required'),
				xeroAccountXeroID: yup.string().required('Xero Account is Required'),
			})
		)
		.min(1, 'Must have at least 1 line item'),
})

const InvoicePrepareLineItems = () => {
	const context = React.useContext(AppContext)
	const [pageStatus, setPageStatus] = React.useState<PageStatus>('Loading')
	const navigate = useNavigate()
	const location = useLocation()

	const match = useMatch('/invoice/:invoiceID/narration')
	const urlInvoiceID = match?.params.invoiceID || ''

	const [invoice, setInvoice] = React.useState<Invoice | null>(null)
	const [xeroAccounts, setXeroAccounts] = React.useState<XeroAccount[] | null>(null)
	const [xeroTaxRates, setXeroTaxRates] = React.useState<XeroTaxRate[] | null>(null)
	const [historyIsOpen, setHistoryIsOpen] = React.useState(false)
	const [message, setMessage] = React.useState<string>('')

	React.useEffect(() => {
		const getData = async () => {
			const invoiceRes = await Request.get(`invoice?where=InvoiceID==${urlInvoiceID}`)
			if (invoiceRes.data.success && invoiceRes.data.invoices.length === 1) {
				const invoice = invoiceRes.data.invoices[0] as Invoice
				setInvoice({ ...invoice, lineItems: invoice.lineItems?.sort((a, b) => a.order - b.order) }) // just in case it doesn't come back sorted from the back-end
				const [accountRes, rateRes] = await Promise.all([
					Request.get(`xeroaccount?where=XeroOrgID==${invoice.xeroOrgID}&where=XeroAccountClass==REVENUE`),
					Request.get(`xerotaxrate?where=XeroOrgID==${invoice.xeroOrgID}`),
				])
				setXeroAccounts(accountRes.data.xeroAccounts)
				setXeroTaxRates(rateRes.data.xeroTaxRates)
				setPageStatus('Ready')
			} else {
				setPageStatus('Error')
			}
		}

		setPageStatus('Loading')
		if (context.appState.authState.isLoggedIn && urlInvoiceID) {
			getData()
		}
	}, [context.appState.authState, urlInvoiceID])

	const getInvoice = React.useCallback(async () => {
		const invoiceRes = await Request.get(`invoice?where=InvoiceID==${urlInvoiceID}`)
		if (invoiceRes.data.success && invoiceRes.data.invoices.length === 1) {
			const invoice = invoiceRes.data.invoices[0] as Invoice
			setInvoice({ ...invoice, lineItems: invoice.lineItems?.sort((a, b) => a.order - b.order) }) // just in case it doesn't come back sorted from the back-end
			if (invoice.xeroID) {
				setMessage('')
			}
		}
	}, [urlInvoiceID])

	useInterval(
		() => {
			getInvoice()
		},
		invoice && !invoice.xeroID ? 5000 : null
	)

	if (pageStatus === 'Loading') {
		return (
			<div className="center-flex p-5">
				<Loading show />
			</div>
		)
	}

	const currentAccountBillySettings = context.appState.accounts.find(
		(acc) => acc.accountID === context.appState.authState.attributes?.['custom:currentAccountId']
	)?.billySettings

	if (pageStatus === 'Error' || invoice === null || xeroAccounts === null || xeroTaxRates === null || !currentAccountBillySettings) {
		return (
			<div className="center-flex flex-column p-5">
				<h3>Error loading invoice</h3>
				<Button
					variant="primary"
					onClick={() => {
						navigate('/invoices')
					}}
				>
					Back to Invoices
				</Button>
			</div>
		)
	}

	const currentOrg = currentAccountBillySettings.orgSettings.find((os) => os.xeroOrgID === invoice.xeroOrgID)
	if (!currentOrg) {
		return (
			<div className="center-flex flex-column p-5">
				<h3>No billy settings found for this org</h3>
				<Button
					variant="primary"
					onClick={() => {
						navigate('/invoices')
					}}
				>
					Back to Invoices
				</Button>
			</div>
		)
	}

	const onSubmit = async ({ redirect, ...values }: Invoice & { redirect: string }) => {
		// we only want to force it to not go in the queue if we are hitting 'previous' and no line items are already in xero
		const triggerQueueOverride = redirect.includes('allocation') ? false : undefined
		const res = await Request.post(
			`invoice/${invoice.invoiceID}/lines`,
			{ invoiceLines: values.lineItems?.map((li, index) => ({ ...li, order: index })), triggerQueueOverride } // update the order
		)
		if (res.data.success) {
			navigate(redirect)
		}
	}

	// Description, account, tax rate, all others
	const colWidthSmallCol = [5, 2, 2, 3]
	// Number fields, options toggle
	const colWidthXSmallCol = [11, 1]
	// 3x number field
	const colWidthThreeEven = [4, 4, 4]

	const accountOptions = getAccountOptions(xeroAccounts)
	const taxRateOptions = getTaxRateOptions(xeroTaxRates)

	const totals = invoice.lineItems?.reduce(
		(total, lineItem) => {
			const { unitAmount, taxAmount, lineAmount } = getTotalsFromTaxType(
				lineItem.unitAmount,
				invoice.lineAmountTypes,
				invoice.xeroOrg.version!,
				lineItem.xeroAccountXeroID,
				xeroAccounts,
				lineItem.xeroTaxRateType,
				xeroTaxRates
			)
			return { unitAmount: total.unitAmount + unitAmount, taxAmount: total.taxAmount + taxAmount, lineAmount: total.lineAmount + lineAmount }
		},
		{ unitAmount: 0, taxAmount: 0, lineAmount: 0 }
	) || { unitAmount: 0, taxAmount: 0, lineAmount: 0 }

	const state = invoiceState(invoice.status, invoice.sentToContact)
	const canEdit = state === 'draft' || state === 'awaiting-approval'

	return (
		<Container fluid className="prep-items-screen">
			{message && <Alert variant="info">{message}</Alert>}
			<Formik initialValues={{ ...invoice, redirect: '' }} validationSchema={invoiceValidationSchema} onSubmit={onSubmit}>
				{({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setValues, setFieldValue, setFieldTouched }) => {
					const insertLineItem = (desc: string) => {
						setValues((prev) => {
							const remainingAmount = totals.unitAmount - roundToTwo(values.lineItems?.reduce((acc, curr) => acc + curr.unitAmount, 0) || 0)
							const remainingAmounts = getTotalsFromTaxType(
								remainingAmount,
								values.lineAmountTypes,
								invoice.xeroOrg.version!,
								currentOrg.servicesXeroAccountXeroID,
								xeroAccounts,
								undefined,
								xeroTaxRates
							)
							return {
								...prev,
								lineItems: [
									...(prev.lineItems || []),
									{
										description: desc,
										order: (prev.lineItems || []).length,
										taxAmount: remainingAmounts.taxAmount > 0 ? roundToTwo(remainingAmounts.taxAmount) : 0,
										unitAmount: remainingAmounts.unitAmount > 0 ? roundToTwo(remainingAmounts.unitAmount) : 0,
										lineAmount: remainingAmounts.lineAmount > 0 ? roundToTwo(remainingAmounts.lineAmount) : 0,
										discountAmount: 0,
										discountRate: 0,
										quantity: 1,
										invoiceLineID: '',
										invoiceID: urlInvoiceID,
										xeroAccountXeroID: currentOrg.servicesXeroAccountXeroID,
										xeroTaxRateType: 'OUTPUT',
										xeroID: null,
									},
								],
							}
						})
						setFieldTouched('lineItems', true)
					}

					const saveOnNext = touched.lineItems || (values.lineItems?.filter((li) => li.xeroID).length || 0) === 0

					return (
						<>
							<Row className="background-light align-items-center py-3">
								<Col>
									<Row className="pb-3 align-items-center">
										<Col sm="auto">
											<h4 className="mb-0">Invoice Allocation</h4>
										</Col>
										{location.state && location.state.queryString && (
											<Col>
												<Link to={`/invoices${location.state.queryString}`}>Back to Invoices</Link>
											</Col>
										)}
										<Col />
										<Col sm="auto">
											<span>
												Status: <span className="span-bold">{state}</span>
											</span>
										</Col>
									</Row>
									<Row className="align-items-center">
										<Col sm={8}>
											<InvoiceInfoCard invoice={invoice} setInvoice={setInvoice} />
										</Col>
										<Col />
										<Col sm="auto">
											<Button
												variant="secondary"
												className="bottom-button"
												disabled={isSubmitting || !canEdit}
												onClick={() => {
													setHistoryIsOpen(true)
												}}
											>
												Insert Previous
												<br />
												Line Item
											</Button>
										</Col>
									</Row>
								</Col>
							</Row>
							<Row className="line-items-body p-3">
								<Col className="line-items-body-col">
									<Row className="line-item-header py-3">
										<Col sm={colWidthSmallCol[0]}>Item</Col>
										<Col sm={colWidthSmallCol[1]}>Xero Account</Col>
										<Col sm={colWidthSmallCol[2]}>Tax Rate</Col>
										<Col sm={colWidthSmallCol[3]}>
											<Row>
												<Col sm={colWidthXSmallCol[0]}>
													<Row>
														<Col sm={colWidthThreeEven[0]} className="d-flex justify-content-end">
															Tax
														</Col>
														<Col sm={colWidthThreeEven[1]} className="d-flex justify-content-end">
															Amount
														</Col>
														<Col sm={colWidthThreeEven[2]} className="d-flex justify-content-end">
															Total
														</Col>
													</Row>
												</Col>
												<Col sm={colWidthXSmallCol[1]}></Col>
											</Row>
										</Col>
									</Row>
									{values.lineItems?.map((lineItem, index) => {
										return (
											<Row key={index} className="line-item-row p-0 align-items-center">
												<Col sm={colWidthSmallCol[0]}>
													<Row>
														<Col className="row-number center-flex py-3">{index + 1}</Col>
														<Col className="p-3">
															<FormText
																name={`lineItems.${index}.description`}
																value={lineItem.description}
																onChange={handleChange}
																onBlur={handleBlur}
																feedback={errorMsgFromFormik(touched, errors, `lineItems.${index}.description`)}
																isInvalid={!!errorMsgFromFormik(touched, errors, `lineItems.${index}.description`)}
																labelCol={0}
																alignCenter
																as={'textarea'}
																rows={lineItem.description.split('\n').length}
																className="textarea-auto-height"
																onInput={(e) => {
																	e.currentTarget.style.height = '1px'
																	e.currentTarget.style.height = e.currentTarget.scrollHeight + 'px'
																}}
																disabled={!canEdit}
															/>
														</Col>
													</Row>
												</Col>
												<Col sm={colWidthSmallCol[1]}>
													<FormSelect
														name={`lineItems.${index}.xeroAccountXeroID`}
														options={accountOptions}
														value={lineItem.xeroAccountXeroID}
														onChange={(e) => {
															const value = e ? (e as SelectOption<string>).value : ''
															const roundedValue = roundToTwo(
																invoice.lineAmountTypes === 'Exclusive' ? lineItem.unitAmount : lineItem.lineAmount
															)
															const { taxAmount, unitAmount, lineAmount } = getTotalsFromTaxType(
																roundedValue,
																invoice.lineAmountTypes,
																invoice.xeroOrg.version!,
																value,
																xeroAccounts,
																lineItem.xeroTaxRateType,
																xeroTaxRates
															)
															setValues((prev) => {
																const lineItemCopy = [...(prev.lineItems || [])]
																lineItemCopy[index].taxAmount = roundToTwo(taxAmount)
																lineItemCopy[index].unitAmount = roundToTwo(unitAmount)
																lineItemCopy[index].lineAmount = roundToTwo(lineAmount)
																lineItemCopy[index].xeroAccountXeroID = value
																return { ...prev, lineItems: lineItemCopy }
															})
															setFieldTouched('lineItems', true)
														}}
														onBlur={handleBlur}
														feedback={errorMsgFromFormik(touched, errors, `lineItems.${index}.xeroAccountXeroID`)}
														labelCol={0}
														isDisabled={!canEdit}
													/>
												</Col>
												<Col sm={colWidthSmallCol[2]}>
													<FormSelect
														name={`lineItems.${index}.xeroTaxRateType`}
														options={taxRateOptions}
														value={lineItem.xeroTaxRateType}
														onChange={(e) => {
															const value = e ? (e as SelectOption<string>).value : ''
															const roundedValue = roundToTwo(
																invoice.lineAmountTypes === 'Exclusive' ? lineItem.unitAmount : lineItem.lineAmount
															)
															const { taxAmount, unitAmount, lineAmount } = getTotalsFromTaxType(
																roundedValue,
																invoice.lineAmountTypes,
																invoice.xeroOrg.version!,
																lineItem.xeroAccountXeroID,
																xeroAccounts,
																value,
																xeroTaxRates
															)
															setValues((prev) => {
																const lineItemCopy = [...(prev.lineItems || [])]
																lineItemCopy[index].taxAmount = roundToTwo(taxAmount)
																lineItemCopy[index].unitAmount = roundToTwo(unitAmount)
																lineItemCopy[index].lineAmount = roundToTwo(lineAmount)
																lineItemCopy[index].xeroTaxRateType = value
																return { ...prev, lineItems: lineItemCopy }
															})
															setFieldTouched('lineItems', true)
														}}
														onBlur={handleBlur}
														feedback={errorMsgFromFormik(touched, errors, `lineItems.${index}.xeroTaxRateType`)}
														labelCol={0}
														isDisabled={!canEdit}
													/>
												</Col>
												<Col sm={colWidthSmallCol[3]}>
													<Row>
														<Col sm={colWidthXSmallCol[0]}>
															<Row>
																<Col sm={colWidthThreeEven[0]}>
																	<FormNumber name={`lineItems.${index}.taxAmount`} value={lineItem.taxAmount} disabled />
																</Col>
																<Col sm={colWidthThreeEven[1]}>
																	<FormNumber
																		name={`lineItems.${index}.unitAmount`}
																		value={lineItem.unitAmount}
																		onChange={(value) => {
																			const roundedValue = roundToTwo(value)
																			const { taxAmount, unitAmount, lineAmount } = getTotalsFromTaxType(
																				roundedValue,
																				invoice.lineAmountTypes,
																				invoice.xeroOrg.version!,
																				lineItem.xeroAccountXeroID,
																				xeroAccounts,
																				lineItem.xeroTaxRateType,
																				xeroTaxRates
																			)
																			setValues((prev) => {
																				const lineItemCopy = [...(prev.lineItems || [])]
																				lineItemCopy[index].taxAmount = roundToTwo(taxAmount)
																				lineItemCopy[index].unitAmount = roundToTwo(unitAmount)
																				lineItemCopy[index].lineAmount = roundToTwo(lineAmount)
																				return { ...prev, lineItems: lineItemCopy }
																			})
																			setFieldTouched('lineItems', true)
																		}}
																		onBlur={handleBlur}
																		onFocus={(e) => e.target.select()}
																		min={0}
																		max={1000000}
																		disabled={values.lineAmountTypes === 'Inclusive' || !canEdit}
																	/>
																</Col>
																<Col sm={colWidthThreeEven[2]}>
																	<FormNumber
																		name={`lineItems.${index}.lineAmount`}
																		value={lineItem.lineAmount}
																		onChange={(value) => {
																			const roundedValue = roundToTwo(value)
																			const { taxAmount, unitAmount, lineAmount } = getTotalsFromTaxType(
																				roundedValue,
																				invoice.lineAmountTypes,
																				invoice.xeroOrg.version!,
																				lineItem.xeroAccountXeroID,
																				xeroAccounts,
																				lineItem.xeroTaxRateType,
																				xeroTaxRates
																			)
																			setValues((prev) => {
																				const lineItemCopy = [...(prev.lineItems || [])]
																				lineItemCopy[index].taxAmount = roundToTwo(taxAmount)
																				lineItemCopy[index].unitAmount = roundToTwo(unitAmount)
																				lineItemCopy[index].lineAmount = roundToTwo(lineAmount)
																				return { ...prev, lineItems: lineItemCopy }
																			})
																			setFieldTouched('lineItems', true)
																		}}
																		onBlur={handleBlur}
																		onFocus={(e) => e.target.select()}
																		min={0}
																		max={1000000}
																		disabled={values.lineAmountTypes !== 'Inclusive' || !canEdit}
																	/>
																</Col>
															</Row>
														</Col>
														<Col sm={colWidthXSmallCol[1]} className="pl-0 pr-2 py-0 center-flex">
															{canEdit && (
																<Dropdown>
																	<Dropdown.Toggle as="div" className="line-options-toggle">
																		<MoreHorizontal size={20} />
																	</Dropdown.Toggle>
																	<Dropdown.Menu>
																		<Dropdown.Item
																			onClick={() => {
																				setValues((prev) => {
																					const newLines = prev.lineItems!.filter((_, i) => i !== index)
																					const dummyLineItem = [
																						{
																							description: 'Must have at least one line item',
																							order: 0,
																							taxAmount: 0,
																							unitAmount: 0,
																							lineAmount: 0,
																							discountAmount: 0,
																							discountRate: 0,
																							quantity: 1,
																							invoiceLineID: '',
																							invoiceID: urlInvoiceID,
																							xeroAccountXeroID: currentOrg.servicesXeroAccountXeroID,
																							xeroTaxRateType: 'OUTPUT',
																							xeroID: null,
																						},
																					]
																					return {
																						...prev,
																						lineItems: newLines.length === 0 ? dummyLineItem : newLines,
																					}
																				})
																				setFieldTouched('lineItems', true)
																			}}
																		>
																			Delete
																		</Dropdown.Item>
																		{index !== 0 && (
																			<Dropdown.Item
																				onClick={() => {
																					setValues((prev) => {
																						const lineItemCopy = [...(prev.lineItems || [])]
																						const temp = lineItemCopy[index]
																						lineItemCopy[index] = lineItemCopy[index - 1]
																						lineItemCopy[index - 1] = temp
																						return { ...prev, lineItems: lineItemCopy }
																					})
																					setFieldTouched('lineItems', true)
																				}}
																			>
																				Move Up
																			</Dropdown.Item>
																		)}
																		{index !== values.lineItems!.length - 1 && (
																			<Dropdown.Item
																				onClick={() => {
																					setValues((prev) => {
																						const lineItemCopy = [...(prev.lineItems || [])]
																						const temp = lineItemCopy[index]
																						lineItemCopy[index] = lineItemCopy[index + 1]
																						lineItemCopy[index + 1] = temp
																						return { ...prev, lineItems: lineItemCopy }
																					})
																					setFieldTouched('lineItems', true)
																				}}
																			>
																				Move Down
																			</Dropdown.Item>
																		)}
																	</Dropdown.Menu>
																</Dropdown>
															)}
														</Col>
													</Row>
												</Col>
											</Row>
										)
									})}
									<Row className="line-item-footer">
										<Col className="center-flex py-2">
											<Button
												variant="secondary"
												className="px-2 py-1"
												onClick={() => {
													insertLineItem('')
												}}
												disabled={!canEdit}
											>
												Add Line Item
											</Button>
										</Col>
									</Row>
								</Col>
							</Row>
							<Row className="totals-bottom-panel p-3">
								<Col>
									<Row className="totals-header pb-2">
										<Col sm={colWidthSmallCol[0] + colWidthSmallCol[1] + colWidthSmallCol[2]}></Col>
										<Col sm={colWidthSmallCol[3]}>
											<Row>
												<Col sm={colWidthXSmallCol[0]}>
													<Row>
														<Col sm={colWidthThreeEven[1]} className="d-flex justify-content-end">
															Tax
														</Col>
														<Col sm={colWidthThreeEven[2]} className="d-flex justify-content-end">
															Amount
														</Col>
														<Col sm={colWidthThreeEven[3]} className="d-flex justify-content-end">
															Total
														</Col>
													</Row>
												</Col>
												<Col sm={colWidthXSmallCol[1]}></Col>
											</Row>
										</Col>
									</Row>
									<Row className="align-items-center pb-2">
										<Col sm={colWidthSmallCol[0] + colWidthSmallCol[1] + colWidthSmallCol[2]} className="d-flex justify-content-end">
											<span className="span-body-2 span-extra span-navy">USER TOTALS:</span>
										</Col>
										<Col sm={colWidthSmallCol[3]}>
											<Row>
												<Col sm={colWidthXSmallCol[0]}>
													<Row>
														<Col sm={colWidthThreeEven[1]} className="d-flex justify-content-end">
															<FormNumber
																name="runningTaxTotal"
																value={roundToTwo(values.lineItems?.reduce((acc, curr) => acc + curr.taxAmount, 0) || 0)}
																disabled
															/>
														</Col>
														<Col sm={colWidthThreeEven[2]} className="d-flex justify-content-end">
															<FormNumber
																name="runningServicesTotal"
																value={roundToTwo(values.lineItems?.reduce((acc, curr) => acc + curr.unitAmount, 0) || 0)}
																disabled
															/>
														</Col>
														<Col sm={colWidthThreeEven[3]} className="d-flex justify-content-end">
															<FormNumber
																name="runningTotal"
																value={roundToTwo(values.lineItems?.reduce((acc, curr) => acc + curr.lineAmount, 0) || 0)}
																disabled
															/>
														</Col>
													</Row>
												</Col>
												<Col sm={colWidthXSmallCol[1]}></Col>
											</Row>
										</Col>
									</Row>
									<Row className="align-items-center pb-4">
										<Col sm={colWidthSmallCol[0] + colWidthSmallCol[1] + colWidthSmallCol[2]} className="d-flex justify-content-end">
											<span className="span-body-2 span-extra span-navy">ACTUAL (REQUIRED) TOTALS:</span>
										</Col>
										<Col sm={colWidthSmallCol[3]}>
											<Row>
												<Col sm={colWidthXSmallCol[0]}>
													<Row>
														<Col sm={colWidthThreeEven[1]} className="d-flex justify-content-end"></Col>
														<Col sm={colWidthThreeEven[2]} className="d-flex justify-content-end">
															<FormNumber
																className={
																	roundToTwo(invoice.minimumTotal) !==
																	roundToTwo(values.lineItems?.reduce((acc, curr) => acc + curr.unitAmount, 0) || 0)
																		? 'is-invalid-total'
																		: ''
																}
																name="total"
																value={roundToTwo(invoice.minimumTotal)}
																disabled
															/>
														</Col>
														<Col sm={colWidthThreeEven[3]} className="d-flex justify-content-end"></Col>
													</Row>
												</Col>
												<Col sm={colWidthXSmallCol[1]}></Col>
											</Row>
										</Col>
									</Row>
									<Row className="button-row pt-3">
										<Col />
										{saveOnNext && !invoice.xeroID && <Col>Waiting for Xero to Proceed</Col>}
										<Col sm="auto">
											<Button
												variant="secondary"
												className="bottom-button"
												disabled={isSubmitting || !canEdit}
												onClick={async () => {
													setFieldValue('redirect', `/invoice/${values.invoiceID}/allocation`)

													touched.lineItems ? handleSubmit() : navigate(`/invoice/${values.invoiceID}/allocation`)
												}}
											>
												Previous
											</Button>
										</Col>
										<Col sm="auto">
											<Button
												variant="primary"
												className="bottom-button"
												onClick={() => {
													setFieldValue('redirect', `/invoice/${values.invoiceID}`)
													saveOnNext ? handleSubmit() : navigate(`/invoice/${values.invoiceID}`)
												}}
												disabled={
													isSubmitting ||
													!canEdit ||
													roundToTwo(values.lineItems?.reduce((acc, curr) => acc + curr.unitAmount, 0) || 0) !==
														roundToTwo(invoice.minimumTotal) ||
													(saveOnNext && !invoice.xeroID)
												}
											>
												{saveOnNext ? 'Save to Xero' : 'Next'}
											</Button>
										</Col>
									</Row>
								</Col>
							</Row>
							<LineItemHistoryPanel
								isOpen={historyIsOpen}
								onClose={() => setHistoryIsOpen(false)}
								currentInvoiceID={invoice.invoiceID}
								currentInvoiceClientID={invoice.clientID || ''}
								insertLineItem={insertLineItem}
							/>
						</>
					)
				}}
			</Formik>
		</Container>
	)
}

const getAccountOptions = (xeroAccounts: XeroAccount[]) => {
	return xeroAccounts.sort((a, b) => Number(a.code) - Number(b.code)).map((ra) => ({ label: `(${ra.code}) ${ra.name}`, value: ra.xeroID! }))
}
const getTaxRateOptions = (xeroTaxRates: XeroTaxRate[]) => {
	return xeroTaxRates
		.filter((xtr) => xtr.canApplyToRevenue)
		.sort((a, b) => a.name!.localeCompare(b.name!))
		.map((xtr) => ({ label: `${xtr.name} (${roundToTwo(Number(xtr.effectiveRate) * 100)}%)`, value: xtr.taxType! }))
}

export default InvoicePrepareLineItems
